import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, FileText, KanbanSquare, MapPin, Ruler, Hash, Pencil, Trash2, AlertTriangle, Handshake, FileSignature, LineChart, Sofa, Camera, CalendarClock, ClipboardList, BadgeEuro, UserCheck, PenLine, Landmark, CheckCircle2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { STAGE_META, PROPERTY_TYPE_LABELS, fmtPrice, nextStage } from "@/lib/realestateConstants";
import { SpaceSection } from "@/components/SpaceSection";
import { StageStepper } from "@/components/realestate/StageStepper";
import { ChecklistItem } from "@/components/realestate/ChecklistItem";
import { VisitsPanel } from "@/components/realestate/VisitsPanel";
import { EditPropertyDialog } from "@/components/realestate/EditPropertyDialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const ICONS = { Handshake, FileSignature, LineChart, Sofa, Camera, CalendarClock, ClipboardList, BadgeEuro, UserCheck, PenLine, Landmark };

const isDone = (item, property, files) => {
  if (property.tasks_by_item?.[item.key]?.status === "done") return true;
  if (item.type === "doc" || item.type === "docs") return files.length > 0;
  if (item.type === "text" || item.type === "link") return !!property.items?.[item.key]?.value?.trim();
  if (item.type === "form") return !!(property[item.key === "datos_vendedor" ? "seller" : "buyer"]?.name || "").trim();
  if (item.type === "visits") return (property.visits?.length || 0) > 0;
  return false;
};

const SectionBlock = ({ section, property, onChanged, canWrite, members }) => {
  const Icon = ICONS[section.icon] || ClipboardList;
  const done = section.items.filter((it) => isDone(it, property, property.files_by_item?.[it.key] || [])).length;
  return (
    <Card className="p-4 space-y-3" data-testid={`section-${section.key}`}>
      <div className="flex items-center gap-2">
        <span className="h-8 w-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><Icon size={16} /></span>
        <h3 className="font-semibold text-sm flex-1">{section.label}</h3>
        <span className={`text-xs font-medium ${done === section.items.length ? "text-emerald-600" : "text-muted-foreground"}`}>{done}/{section.items.length}</span>
      </div>
      <div className="grid gap-2 md:grid-cols-2">
        {section.items.map((item) => {
          const files = property.files_by_item?.[item.key] || [];
          const wide = item.type === "visits" || item.type === "form" || item.type === "text";
          return (
            <div key={item.key} className={wide ? "md:col-span-2" : ""}>
              <ChecklistItem property={property} item={item} files={files} done={isDone(item, property, files)} onChanged={onChanged} canWrite={canWrite} members={members}>
                {item.type === "visits" && <VisitsPanel property={property} visits={property.visits || []} onChanged={onChanged} canWrite={canWrite} />}
              </ChecklistItem>
            </div>
          );
        })}
      </div>
    </Card>
  );
};

export default function PropertyPage() {
  const { id, propertyId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [activeStage, setActiveStage] = useState(null);
  const [editOpen, setEditOpen] = useState(false);

  const { data: ws } = useQuery({ queryKey: ["workspace", id], queryFn: async () => (await api.get(`/workspaces/${id}`)).data });
  const { data: tpl } = useQuery({ queryKey: ["realestate-template"], queryFn: async () => (await api.get("/realestate/template")).data, staleTime: Infinity });
  const { data: property, isLoading } = useQuery({ queryKey: ["property", propertyId], queryFn: async () => (await api.get(`/properties/${propertyId}`)).data });
  const { data: members = [] } = useQuery({ queryKey: ["members", id], queryFn: async () => (await api.get(`/workspaces/${id}/members`)).data });

  const refresh = () => { qc.invalidateQueries({ queryKey: ["property", propertyId] }); qc.invalidateQueries({ queryKey: ["properties", id] }); qc.invalidateQueries({ queryKey: ["tasks"] }); };
  const canWrite = ws?.my_role && ws.my_role !== "guest";
  const isAdmin = ws?.my_role === "owner" || ws?.my_role === "admin";

  if (isLoading || !property || !tpl) return <SpaceSection title="Inmobiliaria"><p className="text-sm text-muted-foreground py-10 text-center">Cargando ficha…</p></SpaceSection>;

  const stage = activeStage || (property.stage === "vendido" ? "postventa" : property.stage);
  const stageDef = tpl.workflow.find((w) => w.stage === stage);
  const meta = STAGE_META[property.stage];
  const prog = property.progress?.[property.stage];
  const next = nextStage(property.stage);
  const hasBuyer = !!property.buyer?.name?.trim();
  const nextBlocked = next && (next === "postventa" || next === "vendido") && !hasBuyer;

  const advance = async () => {
    const pending = prog?.missing?.length || 0;
    const msg = pending ? `Quedan ${pending} elementos obligatorios pendientes en ${meta.label}. ¿Pasar a ${STAGE_META[next].label} de todas formas?` : `¿Pasar el inmueble a ${STAGE_META[next].label}?`;
    if (!window.confirm(msg)) return;
    try {
      await api.put(`/properties/${propertyId}/stage`, { stage: next });
      toast.success(`Inmueble en ${STAGE_META[next].label}`);
      setActiveStage(null);
      refresh();
    } catch (err) { toast.error(apiError(err)); }
  };

  const remove = async () => {
    if (!window.confirm(`¿Eliminar "${property.title}" y todas sus visitas? Los documentos irán a la papelera.`)) return;
    try { await api.delete(`/properties/${propertyId}`); toast.success("Inmueble eliminado"); navigate(`/space/${id}/realestate`); } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <SpaceSection title="Inmobiliaria" actions={
      <Button asChild size="sm" variant="ghost"><Link to={`/space/${id}/realestate`} data-testid="property-back-link"><ArrowLeft size={15} className="mr-1" /> Pipeline</Link></Button>
    }>
      <Card className="p-5 space-y-4" data-testid="property-header">
        <div className="flex flex-wrap items-start gap-3">
          <div className="flex-1 min-w-[240px] space-y-1">
            <div className="flex items-center gap-2 text-xs text-muted-foreground"><Hash size={12} /> {property.ref} <Badge variant="secondary" className="text-[10px]">{PROPERTY_TYPE_LABELS[property.property_type]}</Badge>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${meta.bg} ${meta.text}`} data-testid="property-stage-badge">{meta.label}</span></div>
            <h2 className="font-heading text-xl font-bold leading-tight" data-testid="property-title">{property.title}</h2>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
              {(property.address || property.city) && <span className="flex items-center gap-1"><MapPin size={13} /> {[property.address, property.city].filter(Boolean).join(", ")}</span>}
              {property.surface_m2 && <span className="flex items-center gap-1"><Ruler size={13} /> {property.surface_m2} m²</span>}
              {property.catastro_ref && <span className="font-mono text-xs">RC {property.catastro_ref}</span>}
            </div>
          </div>
          <div className="text-right space-y-1">
            <p className="font-heading text-2xl font-bold">{fmtPrice(property.price)}</p>
            <div className="flex gap-1 justify-end">
              {property.project_id && <Button asChild size="sm" variant="outline" className="h-8"><Link to={`/space/${id}/project/${property.project_id}`} data-testid="property-project-btn"><KanbanSquare size={13} className="mr-1" /> Proyecto · Gantt</Link></Button>}
              <Button asChild size="sm" variant="outline" className="h-8"><Link to={`/space/${id}/realestate/${propertyId}/dossier`} data-testid="property-dossier-btn"><FileText size={13} className="mr-1" /> Dossier PDF</Link></Button>
              {canWrite && <Button size="sm" variant="outline" className="h-8" onClick={() => setEditOpen(true)} data-testid="property-edit-btn"><Pencil size={13} className="mr-1" /> Editar</Button>}
              {isAdmin && <Button size="sm" variant="outline" className="h-8 text-destructive" onClick={remove} data-testid="property-delete-btn"><Trash2 size={13} /></Button>}
            </div>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3 text-sm">
          <div className="rounded-lg bg-secondary/50 p-3"><p className="text-[11px] uppercase tracking-wider text-muted-foreground">Vendedor</p><p className="font-medium">{property.seller?.name || <span className="text-amber-700">Sin datos</span>}</p>{property.seller?.phone && <p className="text-xs text-muted-foreground">{property.seller.phone}{property.seller.email && ` · ${property.seller.email}`}</p>}</div>
          <div className="rounded-lg bg-secondary/50 p-3"><p className="text-[11px] uppercase tracking-wider text-muted-foreground">Comprador</p><p className="font-medium">{property.buyer?.name || <span className="text-muted-foreground">Aún no hay comprador</span>}</p>{property.buyer?.phone && <p className="text-xs text-muted-foreground">{property.buyer.phone}{property.buyer.email && ` · ${property.buyer.email}`}</p>}</div>
        </div>
        <StageStepper property={property} active={stage} onSelect={setActiveStage} />
        {property.stage !== "vendido" && (
          <div className="flex flex-wrap items-center gap-3 rounded-lg border p-3" data-testid="stage-actions">
            {prog?.missing?.length ? (
              <p className="text-xs text-amber-700 flex items-start gap-1.5 flex-1 min-w-[200px]"><AlertTriangle size={14} className="shrink-0 mt-0.5" /><span><strong>{prog.missing.length} pendientes en {meta.label}:</strong> {prog.missing.slice(0, 4).join(", ")}{prog.missing.length > 4 && ` y ${prog.missing.length - 4} más`}</span></p>
            ) : (
              <p className="text-xs text-emerald-600 flex items-center gap-1.5 flex-1"><CheckCircle2 size={14} /> Todo lo obligatorio de {meta.label} está completo</p>
            )}
            {canWrite && next && (
              <Button size="sm" onClick={advance} disabled={nextBlocked} data-testid="stage-advance-btn" title={nextBlocked ? "Completa los datos del comprador para continuar" : ""}>
                {next === "vendido" ? "Marcar como vendido" : `Pasar a ${STAGE_META[next].label}`} <ArrowRight size={14} className="ml-1" />
              </Button>
            )}
          </div>
        )}
        {nextBlocked && <p className="text-xs text-muted-foreground -mt-2" data-testid="postventa-locked-hint">La Post venta empieza solo cuando existe un comprador real: rellena “Datos del comprador” en la etapa Venta.</p>}
      </Card>

      <div className="space-y-4" data-testid={`stage-content-${stage}`}>
        {stageDef?.sections.map((s) => <SectionBlock key={s.key} section={s} property={property} onChanged={refresh} canWrite={canWrite} members={members} />)}
      </div>

      <EditPropertyDialog property={property} open={editOpen} onOpenChange={setEditOpen} onChanged={refresh} />
    </SpaceSection>
  );
}
